/** Getting about: nav-grid queries, budgeted A* path requests, path following, turning. */

import { findPath } from '../astar';
import type { Ctx } from '../ctx';
import { angleDiff, atan2 } from '../math';
import { resolveMovement, type Statics } from '../statics';
import { ENEMY } from '../tuning';
import { DT, type Enemy } from '../types';

export function cellFree(statics: Statics, x: number, z: number): boolean {
  const nav = statics.nav;
  const cx = Math.floor(x);
  const cz = Math.floor(z);
  if (cx < 0 || cz < 0 || cx >= nav.width || cz >= nav.height) return false;
  return (nav.blocked[cz * nav.width + cx] ?? 1) === 0;
}

/** Nearest free nav cell centre to a point, searching a small square around it. */
export function nearestFree(statics: Statics, x: number, z: number, reach = 3): [number, number] | null {
  if (cellFree(statics, x, z)) return [Math.floor(x), Math.floor(z)];
  let best: [number, number] | null = null;
  let bestD = Infinity;
  for (let oz = -reach; oz <= reach; oz++) {
    for (let ox = -reach; ox <= reach; ox++) {
      if (!cellFree(statics, x + ox, z + oz)) continue;
      const d = ox * ox + oz * oz;
      if (d < bestD) {
        bestD = d;
        best = [Math.floor(x + ox), Math.floor(z + oz)];
      }
    }
  }
  return best;
}

/**
 * As `nearestFree`, however big the thing in the way: rings outward until one holds a free cell (and half as far
 * again, since a ring's corners are further off than the next ring's sides). For a goal in the middle of a large
 * solid prop - a search party sent to a crash site - this is the free cell at its edge.
 */
export function nearestFreeAnySize(statics: Statics, x: number, z: number): [number, number] | null {
  const limit = Math.max(statics.nav.width, statics.nav.height);
  for (let reach = 4; reach <= limit; reach += 2) {
    if (!nearestFree(statics, x, z, reach)) continue;
    return nearestFree(statics, x, z, Math.ceil(reach * 1.5));
  }
  return null;
}

/** Where a path to this point ends: its own cell, or - when that is inside something solid - the nearest free one. */
function goalCell(statics: Statics, x: number, z: number): [number, number] | null {
  return nearestFree(statics, x, z, 4) ?? nearestFreeAnySize(statics, x, z);
}

export function requestPath(statics: Statics, e: Enemy, x: number, z: number): void {
  const start = nearestFree(statics, e.x, e.z, 2);
  const goal = goalCell(statics, x, z);
  const cells = start && goal ? findPath(statics.nav, start[0], start[1], goal[0], goal[1], 6000) : null;
  e.path = cells ? cells.map(([cx, cz]) => [cx + 0.5, cz + 0.5] as [number, number]) : [];
  e.pathIndex = e.path.length > 1 ? 1 : 0;
}

/** Walk toward a point along a path; returns true on arrival (or when no path exists). */
export function travel(ctx: Ctx, e: Enemy, x: number, z: number, speed: number, arrive = 1.2): boolean {
  const goalDx = x - e.x;
  const goalDz = z - e.z;
  if (goalDx * goalDx + goalDz * goalDz < arrive * arrive) {
    e.moving = false;
    return true;
  }
  const last = e.path[e.path.length - 1];
  let stale = !last || (last[0] - x) * (last[0] - x) + (last[1] - z) * (last[1] - z) > 9;
  if (stale && last && !cellFree(ctx.statics, x, z)) {
    // The goal is inside something solid and big: a path that ends at its edge is as good as it gets, not stale.
    const edge = goalCell(ctx.statics, x, z);
    const dx = edge ? last[0] - edge[0] - 0.5 : 0;
    const dz = edge ? last[1] - edge[1] - 0.5 : 0;
    stale = !edge || dx * dx + dz * dz > 9;
  }
  if (stale) {
    // Paths are the expensive part of the AI: spread requests over ticks.
    if (ctx.world.tick % 4 !== e.id % 4) return false;
    requestPath(ctx.statics, e, x, z);
    if (e.path.length === 0) {
      e.moving = false;
      return true;
    }
  }
  const node = e.path[e.pathIndex];
  if (!node) {
    e.moving = false;
    return true;
  }
  const dx = node[0] - e.x;
  const dz = node[1] - e.z;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d < 0.35) {
    e.pathIndex++;
    return e.pathIndex >= e.path.length;
  }
  const step = Math.min(d, speed * DT);
  const moved = resolveMovement(ctx.statics, e.x + (dx / d) * step, e.z + (dz / d) * step, ENEMY.radius);
  e.travelled += Math.sqrt((moved.x - e.x) * (moved.x - e.x) + (moved.z - e.z) * (moved.z - e.z));
  e.x = moved.x;
  e.z = moved.z;
  e.moving = true;
  turnToward(e, atan2(dx, dz), 8);
  return false;
}

export function turnToward(e: Enemy, angle: number, radPerSecond: number): void {
  const diff = angleDiff(e.facing, angle);
  const max = radPerSecond * DT;
  e.facing += Math.max(-max, Math.min(max, diff));
}
